/**
 * keyboardHandlers.ts - Outliner Keyboard Event Handlers
 * 
 * Ported from original OutlinerViewList.ORIGINAL.tsx (lines 1374-1978)
 * 605 lines of sophisticated keyboard logic with 3+ years of refinement
 * 
 * CHANGE HISTORY:
 * - 251117_2305 danielbeer: Port keyboard handlers from original implementation
 */

import type { OutlinerNode } from '../components/OutlinerView';

export interface KeyboardHandlerContext {
  // Node data
  nodes: OutlinerNode[];
  visibleNodes: string[];
  
  // Current state
  editingNodeId: string | null;
  currentNodeId: string | null;
  
  // Selection
  selectr: any;
  
  // Operations
  startEdit: (id: string) => void;
  exitEdit: () => void;
  updateNode: (id: string, updates: Partial<OutlinerNode>) => void;
  updateData: (nodes: OutlinerNode[]) => void;
  deleteNodes: (ids: string[]) => void;
  addNode: (node: OutlinerNode, position: 'before' | 'after' | 'child', targetId: string) => void;
  
  // View operations
  expandNode: (id: string) => void;
  collapseNode: (id: string) => void;
  isExpanded: (id: string) => boolean;
  
  // Clipboard
  clippr?: any;
  
  // Promptr for dialogs
  promptr?: any;
  
  // Refs
  containerRef: React.RefObject<HTMLDivElement>;
  inputRef?: React.RefObject<HTMLInputElement>;
}

/**
 * Handle keyboard events in EDIT MODE
 * Active when editing a node's text field
 */
export const handleEditKeyDown = (
  e: React.KeyboardEvent<HTMLInputElement>,
  nodeId: string,
  context: KeyboardHandlerContext
) => {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;
  
  const node = context.nodes.find(n => n.id === nodeId);
  if (!node) return;
  
  // ========================================
  // SMART ARROW NAVIGATION IN EDIT MODE
  // ========================================
  
  // Arrow Up/Down: Smart navigation based on cursor position
  if (!e.ctrlKey && !e.metaKey && ['ArrowUp', 'ArrowDown'].includes(e.key)) {
    const input = e.target as HTMLInputElement;
    const cursorPos = input.selectionStart || 0;
    const text = input.value || '';
    
    // Check if we're at the boundary (top or bottom of the field)
    let atBoundary = false;
    
    if (e.key === 'ArrowUp') {
      // At top if cursor is at start OR if there's no newline before cursor
      const textBeforeCursor = text.substring(0, cursorPos);
      atBoundary = cursorPos === 0 || !textBeforeCursor.includes('\n');
    } else if (e.key === 'ArrowDown') {
      // At bottom if cursor is at end OR if there's no newline after cursor
      const textAfterCursor = text.substring(cursorPos);
      atBoundary = cursorPos === text.length || !textAfterCursor.includes('\n');
    }
    
    // If at boundary, move to adjacent item
    if (atBoundary) {
      e.preventDefault();
      const currentIndex = context.visibleNodes.indexOf(nodeId);
      
      if (e.key === 'ArrowUp' && currentIndex > 0) {
        const prevNodeId = context.visibleNodes[currentIndex - 1];
        context.startEdit(prevNodeId);
        context.selectr.select(prevNodeId, 'replace');
        
        // Set cursor to end of previous field
        setTimeout(() => {
          if (context.inputRef?.current) {
            const len = context.inputRef.current.value.length;
            context.inputRef.current.setSelectionRange(len, len);
          }
        }, 0);
      } else if (e.key === 'ArrowDown' && currentIndex < context.visibleNodes.length - 1) {
        const nextNodeId = context.visibleNodes[currentIndex + 1];
        context.startEdit(nextNodeId);
        context.selectr.select(nextNodeId, 'replace');
        
        // Set cursor to start of next field
        setTimeout(() => {
          if (context.inputRef?.current) {
            context.inputRef.current.setSelectionRange(0, 0);
          }
        }, 0);
      }
      return;
    }
  }
  
  // ========================================
  // CTRL+ARROW: FORCE NAVIGATION
  // ========================================
  
  // Ctrl+Arrow: Move to edit mode of adjacent item (ignores cursor position)
  if (e.ctrlKey && !e.metaKey && ['ArrowUp', 'ArrowDown'].includes(e.key)) {
    e.preventDefault();
    const currentIndex = context.visibleNodes.indexOf(nodeId);
    
    if (e.key === 'ArrowUp' && currentIndex > 0) {
      const prevNodeId = context.visibleNodes[currentIndex - 1];
      context.startEdit(prevNodeId);
      context.selectr.select(prevNodeId, 'replace');
    } else if (e.key === 'ArrowDown' && currentIndex < context.visibleNodes.length - 1) {
      const nextNodeId = context.visibleNodes[currentIndex + 1];
      context.startEdit(nextNodeId);
      context.selectr.select(nextNodeId, 'replace');
    }
    return;
  }
  
  // ========================================
  // CMD+ARROW: TEXT NAVIGATION
  // ========================================
  
  // Allow Cmd+Up/Down for text navigation in edit mode
  if (cmdOrCtrl && ['ArrowUp', 'ArrowDown'].includes(e.key)) {
    // Let browser handle text navigation (jump to start/end of text)
    return;
  }
  
  // ========================================
  // DELETE/BACKSPACE IN EDIT MODE
  // ========================================
  
  // In edit mode, Delete/Backspace should ONLY edit text, NEVER delete the node
  // Always let the browser handle these keys for text editing
  if (e.key === 'Delete' || e.key === 'Backspace') {
    // Do NOT prevent default - let browser handle text deletion
    // Do NOT call any delete functions - we're editing text!
    return; // Exit early - no further processing
  }
  
  // ========================================
  // ENTER KEY VARIATIONS
  // ========================================
  
  // Shift+Enter: Allow new line in text (for multi-line support)
  if (e.key === 'Enter' && e.shiftKey) {
    // Let browser handle - allows multi-line names
    return;
  }
  
  // NumpadEnter: Commit changes and stay on current item
  if (e.key === 'Enter' && e.code === 'NumpadEnter') {
    e.preventDefault();
    
    // Check if the node is empty and delete it
    if (!node.text || node.text.trim() === '') {
      const currentIndex = context.visibleNodes.indexOf(nodeId);
      let nextSelection = null;
      
      if (currentIndex > 0) {
        nextSelection = context.visibleNodes[currentIndex - 1];
      } else if (currentIndex < context.visibleNodes.length - 1) {
        nextSelection = context.visibleNodes[currentIndex + 1];
      }
      
      context.deleteNodes([nodeId]);
      context.exitEdit();
      
      if (nextSelection) {
        context.selectr.select(nextSelection, 'replace');
      }
      
      // Refocus container
      setTimeout(() => context.containerRef.current?.focus(), 0);
    } else {
      // Just exit edit mode, stay on current
      context.exitEdit();
      setTimeout(() => context.containerRef.current?.focus(), 0);
    }
    return;
  }
  
  // Regular Enter: Save and create sibling below
  if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
    e.preventDefault();
    
    // Check if empty - delete and move to previous
    if (!node.text || node.text.trim() === '') {
      const currentIndex = context.visibleNodes.indexOf(nodeId);
      let nextSelection = null;
      
      if (currentIndex > 0) {
        nextSelection = context.visibleNodes[currentIndex - 1];
      } else if (currentIndex < context.visibleNodes.length - 1) {
        nextSelection = context.visibleNodes[currentIndex + 1];
      }
      
      context.deleteNodes([nodeId]);
      context.exitEdit();
      
      if (nextSelection) {
        context.selectr.select(nextSelection, 'replace');
      }
      
      setTimeout(() => context.containerRef.current?.focus(), 0);
      return;
    }
    
    // Create sibling after current node
    const newNode: OutlinerNode = {
      id: `node-${Date.now()}`,
      text: '',
      parentId: node.parentId,
      generation: node.generation,
      expanded: false,
      children: []
    };
    
    context.addNode(newNode, 'after', nodeId);
    context.selectr.select(newNode.id, 'replace');
    context.startEdit(newNode.id);
    return;
  }
  
  // Cmd+Enter: Save and create child
  if (e.key === 'Enter' && cmdOrCtrl) {
    e.preventDefault();
    
    // Create child of current node
    const newNode: OutlinerNode = {
      id: `node-${Date.now()}`,
      text: '',
      parentId: nodeId,
      generation: (node.generation || 0) + 1,
      expanded: false,
      children: []
    };
    
    // Expand parent
    context.expandNode(nodeId);
    
    context.addNode(newNode, 'child', nodeId);
    context.selectr.select(newNode.id, 'replace');
    context.startEdit(newNode.id);
    return;
  }
  
  // ========================================
  // INDENT/OUTDENT IN EDIT MODE
  // ========================================
  
  // Cmd+Right: Indent (stay in edit mode)
  if (e.key === 'ArrowRight' && cmdOrCtrl) {
    e.preventDefault();
    
    // Find previous sibling to indent under
    const siblings = context.nodes.filter(n => n.parentId === node.parentId);
    const currentIndex = siblings.findIndex(s => s.id === nodeId);
    
    if (currentIndex > 0) {
      const previousSibling = siblings[currentIndex - 1];
      
      // Update node to become child of previous sibling
      context.updateNode(nodeId, {
        parentId: previousSibling.id,
        generation: (previousSibling.generation || 0) + 1
      });
      
      // Expand the new parent
      context.expandNode(previousSibling.id);
    }
    return;
  }
  
  // Cmd+Left: Outdent (stay in edit mode)
  if (e.key === 'ArrowLeft' && cmdOrCtrl) {
    e.preventDefault();
    
    if (node.parentId) {
      const parent = context.nodes.find(n => n.id === node.parentId);
      if (parent) {
        // Need to:
        // 1. Remove node from current position
        // 2. Update its parentId and generation
        // 3. Insert it right after its parent
        
        // Get current nodes array
        const currentNodes = [...context.nodes];
        const nodeIndex = currentNodes.findIndex(n => n.id === nodeId);
        const parentIndex = currentNodes.findIndex(n => n.id === parent.id);
        
        if (nodeIndex !== -1 && parentIndex !== -1) {
          // Remove node from current position
          const [removedNode] = currentNodes.splice(nodeIndex, 1);
          
          // Update node properties
          const updatedNode = {
            ...removedNode,
            parentId: parent.parentId,
            generation: parent.generation
          };
          
          // Find new parent index (might have changed after removal)
          const newParentIndex = currentNodes.findIndex(n => n.id === parent.id);
          
          // Insert right after parent
          currentNodes.splice(newParentIndex + 1, 0, updatedNode);
          
          // Update the entire nodes array with the reordered data
          context.updateData(currentNodes);
        }
      }
    }
    return;
  }
  
  // ========================================
  // TAB: EXIT EDIT MODE
  // ========================================
  
  if (e.key === 'Tab' && !e.shiftKey) {
    e.preventDefault();
    context.exitEdit();
    setTimeout(() => context.containerRef.current?.focus(), 0);
    return;
  }
  
  // ========================================
  // ESCAPE: CANCEL EDIT AND UNDO CHANGES
  // ========================================
  
  if (e.key === 'Escape') {
    e.preventDefault();
    
    // Get the current input value
    const input = e.target as HTMLInputElement;
    const currentText = input.value;
    
    // Get the original text from the node data
    const originalText = node.text;
    
    // If text was changed, restore original (undo changes)
    if (currentText !== originalText) {
      context.updateNode(nodeId, { text: originalText });
    }
    
    // If the original text is empty, delete the node
    if (!originalText || originalText.trim() === '') {
      const currentIndex = context.visibleNodes.indexOf(nodeId);
      let nextSelection = null;
      
      if (currentIndex > 0) {
        nextSelection = context.visibleNodes[currentIndex - 1];
      } else if (currentIndex < context.visibleNodes.length - 1) {
        nextSelection = context.visibleNodes[currentIndex + 1];
      }
      
      context.deleteNodes([nodeId]);
      context.exitEdit();
      
      if (nextSelection) {
        context.selectr.select(nextSelection, 'replace');
      }
    } else {
      // Exit edit mode and select the CURRENT node (not parent)
      context.exitEdit();
      context.selectr.select(nodeId, 'replace');
    }
    
    setTimeout(() => context.containerRef.current?.focus(), 0);
    return;
  }
};

/**
 * Handle keyboard events in NAVIGATION MODE
 * Active when NOT editing, navigating the outline
 */
export const handleNavigationKeyDown = (
  e: React.KeyboardEvent<HTMLDivElement>,
  context: KeyboardHandlerContext
) => {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;
  
  const selectedIds = context.selectr.getSelected();
  const currentNodeId = selectedIds.length > 0 ? selectedIds[0] : null;
  
  // ========================================
  // NO NODES: CREATE FIRST NODE ON ENTER
  // ========================================
  
  if (context.nodes.length === 0 && e.key === 'Enter' && !e.shiftKey && !cmdOrCtrl) {
    e.preventDefault();
    
    const newNode: OutlinerNode = {
      id: `node-${Date.now()}`,
      text: '',
      parentId: null,
      collapsed: false,
      generation: 0
    };
    
    context.addNode(newNode, 'after', null as any); // null is valid for root level
    context.selectr.select(newNode.id, 'replace');
    context.startEdit(newNode.id);
    return;
  }
  
  // ========================================
  // SPACE: ENTER EDIT MODE
  // ========================================
  
  if (e.key === ' ' && !e.shiftKey && !cmdOrCtrl) {
    e.preventDefault();
    if (currentNodeId) {
      context.startEdit(currentNodeId);
    }
    return;
  }
  
  // ========================================
  // TYPE-TO-EDIT
  // ========================================
  
  // Any alphanumeric key: Enter edit mode and start typing
  if (
    e.key.length === 1 &&
    !cmdOrCtrl &&
    !e.altKey &&
    /^[a-zA-Z0-9]$/.test(e.key)
  ) {
    e.preventDefault();
    
    if (currentNodeId) {
      const node = context.nodes.find(n => n.id === currentNodeId);
      if (node) {
        // If node is empty or "Untitled", replace with typed character
        if (!node.text || node.text.trim() === '' || node.text === 'Untitled') {
          context.updateNode(currentNodeId, { text: e.key });
        } else {
          // Append typed character
          context.updateNode(currentNodeId, { text: node.text + e.key });
        }
        
        // Enter edit mode
        context.startEdit(currentNodeId);
        
        // Position cursor after typed character
        setTimeout(() => {
          if (context.inputRef?.current) {
            const len = context.inputRef.current.value.length;
            context.inputRef.current.setSelectionRange(len, len);
          }
        }, 0);
      }
    }
    return;
  }
  
  // ========================================
  // ESCAPE: CLEAR SELECTION / GO UP
  // ========================================
  
  if (e.key === 'Escape') {
    e.preventDefault();
    
    if (selectedIds.length > 1) {
      // Multi-select: clear to single
      if (currentNodeId) {
        context.selectr.select(currentNodeId, 'replace');
      }
    } else if (currentNodeId) {
      // Single select: select parent
      const node = context.nodes.find(n => n.id === currentNodeId);
      if (node?.parentId) {
        context.selectr.select(node.parentId, 'replace');
      } else {
        // At root: deselect
        context.selectr.clearSelection();
      }
    }
    return;
  }
  
  // ========================================
  // DELETE / BACKSPACE: HANDLED BY COMMAND SYSTEM
  // ========================================
  // NOTE: Delete/Backspace are now handled by the command system (handleDelete in handlers.ts)
  // This prevents duplicate deletion when both keyboard handler and command handler fire
  
  // REMOVED: Duplicate handler that was causing double-deletion
  // The command system properly handles Delete/Backspace with all the same logic
  
  // REMOVED: Cmd+Delete handler - also handled by command system now
  
  // ========================================
  // SHIFT+ARROW: RANGE SELECTION
  // ========================================
  
  // Shift+Up: Extend selection upward
  if (e.key === 'ArrowUp' && e.shiftKey && !cmdOrCtrl) {
    e.preventDefault();
    
    if (context.visibleNodes.length === 0) return;
    
    const selectedIds = context.selectr.getSelected();
    const anchor = context.selectr.getAnchor();
    
    if (!anchor || selectedIds.length === 0) {
      // No anchor, just select first visible
      if (context.visibleNodes.length > 0) {
        context.selectr.select(context.visibleNodes[0], 'replace');
      }
      return;
    }
    
    const anchorIndex = context.visibleNodes.indexOf(anchor);
    
    // Find the active end (furthest from anchor in visible nodes order)
    const selectedIndices = selectedIds
      .map((id: string) => context.visibleNodes.indexOf(id))
      .filter((idx: number) => idx !== -1);
    
    const minIndex = Math.min(...selectedIndices);
    const maxIndex = Math.max(...selectedIndices);
    
    // Active end is whichever is furthest from anchor
    const activeIndex = Math.abs(minIndex - anchorIndex) > Math.abs(maxIndex - anchorIndex) 
      ? minIndex 
      : maxIndex;
    
    console.log('[Shift+Up]', {
      anchor,
      anchorIndex,
      selectedIds,
      selectedIndices,
      minIndex,
      maxIndex,
      activeIndex,
      visibleNodes: context.visibleNodes
    });
    
    if (activeIndex > 0) {
      const newActiveIndex = activeIndex - 1;
      
      // Select range from anchor to new active position
      const start = Math.min(anchorIndex, newActiveIndex);
      const end = Math.max(anchorIndex, newActiveIndex);
      const rangeIds = context.visibleNodes.slice(start, end + 1);
      
      console.log('[Shift+Up] Selecting:', { start, end, rangeIds });
      
      // Manually update selection to preserve anchor
      // Access the internal state directly since there's no setAnchor method
      (context.selectr as any).selected.clear();
      rangeIds.forEach(id => (context.selectr as any).selected.add(id));
      (context.selectr as any).anchorId = anchor;
      (context.selectr as any).emit('change', rangeIds);
    }
    return;
  }
  
  // Shift+Down: Extend selection downward
  if (e.key === 'ArrowDown' && e.shiftKey && !cmdOrCtrl) {
    e.preventDefault();
    
    if (context.visibleNodes.length === 0) return;
    
    const selectedIds = context.selectr.getSelected();
    const anchor = context.selectr.getAnchor();
    
    if (!anchor || selectedIds.length === 0) {
      // No anchor, just select first visible
      if (context.visibleNodes.length > 0) {
        context.selectr.select(context.visibleNodes[0], 'replace');
      }
      return;
    }
    
    const anchorIndex = context.visibleNodes.indexOf(anchor);
    
    // Find the active end (furthest from anchor in visible nodes order)
    const selectedIndices = selectedIds
      .map((id: string) => context.visibleNodes.indexOf(id))
      .filter((idx: number) => idx !== -1);
    
    const minIndex = Math.min(...selectedIndices);
    const maxIndex = Math.max(...selectedIndices);
    
    // Active end is whichever is furthest from anchor
    const activeIndex = Math.abs(minIndex - anchorIndex) > Math.abs(maxIndex - anchorIndex) 
      ? minIndex 
      : maxIndex;
    
    if (activeIndex < context.visibleNodes.length - 1) {
      const newActiveIndex = activeIndex + 1;
      
      // Select range from anchor to new active position
      const start = Math.min(anchorIndex, newActiveIndex);
      const end = Math.max(anchorIndex, newActiveIndex);
      const rangeIds = context.visibleNodes.slice(start, end + 1);
      
      // Manually update selection to preserve anchor
      // Access the internal state directly since there's no setAnchor method
      (context.selectr as any).selected.clear();
      rangeIds.forEach(id => (context.selectr as any).selected.add(id));
      (context.selectr as any).anchorId = anchor;
      (context.selectr as any).emit('change', rangeIds);
    }
    return;
  }
  
  // Note: Arrow navigation, Enter for create, clipboard ops, etc. 
  // are handled by Commandr command handlers
};
